import { Grid } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import { ITenderExchangeDetails } from 'src/common/interfaces/states/ITenderExchangeState';
import { hasCashDrawer } from 'src/devices/cash-drawer-operations';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { CartPaymentData } from 'src/items-list/business-logic/service/interfaces/cart-response';
import EmeraldDialog from '../../../common/components/emerald-dialog/emerald-dialog';
import { convertCurrency } from '../../../common/helpers/currency-helper';
import { ReactComponent as Cart } from '../../../common/media/cart.svg';
import { AppState } from '../../../common/redux/configure-store';
import { dismissThankYouModal } from '../../business-logic/tendering-actions';
import { styles } from './thank-you-modal-styles';
import { CashBackList } from './cashback-list-display';

type Props = ReturnType<typeof mapStateToProps> & ThankYouModalState & ThankYouModalProps;
const ThankYouModalComponent: React.FC<Props> = ({
  changeDue,
  cashback,
  cashDrawerOpen,
  isPrintReceipt,
  payments,
  tenderExchangeDetails: { paymentTender, refundTender, isRefundScreenVisible },
  timeout = 3000,
  openTillDrawer,
}: Props): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();

  const isCashDrawerOpen = useRef<boolean>(cashDrawerOpen);
  const grandTotal = useSelector((state: AppState): number => state.tendering.grandTotal);
  const isTrainingMode = useSelector((state: AppState) => state.posMode.trainingMode);
  const isTBRTransaction = useSelector(
    (state: AppState) => state.posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn,
  );
  const refundDue = useSelector((state: AppState) => state.posMode.refundDue) ?? 0;
  useEffect(() => {
    const isCashDrawerClosed = !cashDrawerOpen;
    const wasCashDrawerOpen = isCashDrawerOpen.current;
    if (wasCashDrawerOpen && isCashDrawerClosed && hasCashDrawer()) {
      dismissThankYouModal();
    } else if (!openTillDrawer || !hasCashDrawer() || isTrainingMode) {
      const timer = setTimeout(() => {
        dismissThankYouModal();
      }, timeout);
      return () => clearTimeout(timer);
    }
  }, [cashDrawerOpen, openTillDrawer, timeout, isTrainingMode]);

  let header = t('ReceiptView_Change');

  if (grandTotal < 0) {
    header = t('RefundHeader');
  }

  const isTenderExchange = paymentTender && refundTender && isRefundScreenVisible;
  if (isTenderExchange) {
    header = t('TenderExchange_Command');
  }
  if (isTBRTransaction) {
    changeDue = refundDue == 0 ? changeDue : refundDue;
  }
  return (
    <EmeraldDialog
      open
      fullWidth={true}
      className={classes.component}
      classes={{
        paper: classes.dialogPaper,
      }}
      data-testid='thank-you-dialog'
      onClick={event => {
        const cashBackList = document.querySelector('.cashBackList');
        if (cashBackList?.contains(event.target)) {
          return;
        }
        if (!openTillDrawer || !hasCashDrawer()) {
          dismissThankYouModal();
        }
      }}
    >
      <Grid item xs={12}>
        <Grid
          className={classes.header}
          container
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Grid item>
            <span className={classes.spacing} data-testid='thank-you-header'>
              {header}
            </span>
          </Grid>

          <Grid item>
            <InfoIcon classes={{ root: `${classes.icon} ${classes.info}` }} />
          </Grid>
        </Grid>
        <Grid>
          <p className={`${classes.title} ${classes.noWrap}`} data-testid='money-back'>
            {convertCurrency(changeDue)}
          </p>
          <div className={`${classes.bar} ${classes.barinfo}`} />
          {isTenderExchange && (
            <p
              className={`${classes.title} ${classes.noWrap}`}
              data-testid='tender-exchange-details'
            >
              <span
                className={classes.cashbackHeader}
              >{`${paymentTender?.description} to ${refundTender?.description}`}</span>
            </p>
          )}
          {cashback > 0 && payments && <CashBackList payments={payments} />}
          <p className={`${classes.message} ${classes.noWrap}`}>
            {t('CashPaymentChangeView_Note')}
          </p>
          <Cart className={`${classes.cart}`} />
          <p className={classes.receiptInfo}>
            {!isPrintReceipt ? t('CashPaymentChangeView_DigitalReceiptNote') : null}
          </p>
        </Grid>
      </Grid>
    </EmeraldDialog>
  );
};

interface ThankYouModalState {
  changeDue: number;
  cashback: number;
  cashDrawerOpen: boolean;
  payments: CartPaymentData[] | undefined;
  tenderExchangeDetails: ITenderExchangeDetails;
}

interface ThankYouModalProps {
  isPrintReceipt: boolean;
  timeout?: number;
  openTillDrawer?: boolean;
}

const mapStateToProps = (state: AppState): ThankYouModalState => ({
  changeDue: state.tendering.changeDue,
  cashback: state.tendering.cashback,
  payments: state.itemList.payments,
  cashDrawerOpen: state.cashDrawer.open,
  tenderExchangeDetails: state.tenderExchange.tenderExchangeDetails,
});

// export default ThankYouModal;
const ThankYouModal = connect(mapStateToProps)(ThankYouModalComponent);

export default ThankYouModal;
